import { useCallback, useEffect, useState } from 'react'
import { Button, Checkbox, Radio, RadioGroup } from '@heroui/react'
import { api } from '../lib/api'
import { t as kioskSay } from '../lib/kioskText'
import { explain } from '../lib/staffErrors'
import { nameIn, t, type StaffLanguage } from '../lib/staffText'
import type { Mark, Service } from '../lib/types'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import {
  CODE_RE,
  CodeField,
  cleanNames,
  LangSwitch,
  LoadingLine,
  MoveButtons,
  NamesField,
  Preview,
  ServiceScope,
  hasName,
  inOrder,
  reorder,
  useServiceName,
} from './recordParts'
import { Toggle, useFlash, useSave } from './sectionKit'
import { ChoiceSelect, IconTile, InnerPage, ListCard, ListRow, ListTable, NameCell, NEW, Notice, PageHead, Seg, useOpenRecord } from './settingsKit'
import { EmptyState } from '../ui/parts'
import '../styles/orgset.css'

/**
 * Marks: what else this branch counts about a piece of work.
 *
 * This screen is deliberately the Outcomes screen with one rule missing, and the
 * missing rule is the whole point. An outcome answers "how did this end" and a
 * service with any outcome available requires one at close. A mark answers nothing
 * and is never required: it is there so a branch can count something we did not
 * anticipate — an interpreter was needed, this was a first visit, the customer
 * complained, they were sent by branch 12 — without asking us for a report column.
 *
 * Several land on one piece of work, so the report's mark totals do not add up to
 * the number of visits and never will. Nothing here ever stops a desk finishing.
 *
 * Two kinds live here, told apart by who presses them: the clerk at the desk, or
 * the customer on the feedback tablet. A customer's button is for every service,
 * may carry a score, and there are at most five of them in service (the server's
 * catalog.MaxCustomerMarks) — so the screen says so before anybody tries a sixth.
 * Which kind a mark is, is fixed when it is made: a mark that changed sides would
 * split its own figures across two tables.
 */

/** The server's catalog.MaxCustomerMarks. */
const MAX_CUSTOMER = 5

/** The step of a tablet button being added. */
const NEW_TABLET = 'new-tablet'

/** Example codes, the same in every language. */
const EXAMPLE_CLERK = 'FIRST_VISIT'
const EXAMPLE_CUSTOMER = 'VERY_GOOD'

type Draft = Partial<Mark> & { name: Record<string, string> }
type Editing = { mark: Draft; isNew: boolean }

const blank = (customer: boolean): Draft => ({
  code: '',
  name: {},
  serviceId: '',
  sortOrder: 0,
  active: true,
  customer,
  score: 0,
})

export function Marks({ organisation }: { organisation?: string }) {
  const { lang, say } = useStaffText()
  const [marks, setMarks] = useState<Mark[]>([])
  const [services, setServices] = useState<Service[]>([])
  const [loaded, setLoaded] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [previewLang, setPreviewLang] = useState<StaffLanguage>(lang)
  const serviceName = useServiceName(services)

  const load = useCallback(async () => {
    const listed = await api.marks(organisation)
    setMarks(listed.marks)
    setLoaded(true)
  }, [organisation])

  const { flash, toast } = useFlash()
  const { busy, error, setError, save } = useSave(load, flash)

  useEffect(() => {
    setLoaded(false)
    load().catch((err) => {
      setLoadError(explain(err, lang, 'catalogue.marks.notLoaded'))
      setLoaded(true)
    })
    api
      .catalogueServices(organisation)
      .then((listed) => setServices(listed.services))
      .catch(() => setServices([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, organisation])

  const clerk = inOrder(marks.filter((m) => !m.customer && m.active))
  const buttons = inOrder(marks.filter((m) => m.customer && m.active))
  const retired = inOrder(marks.filter((m) => !m.active))
  const full = buttons.length >= MAX_CUSTOMER

  // The mark open on its own page (#marks/<id>, #marks/new, a tablet button's #marks/new-tablet).
  const rec = useOpenRecord(marks, (m) => m.id, loaded, [NEW_TABLET])
  const open = (m?: Mark, customer = false) => {
    setError('')
    if (m) rec.open(m)
    else rec.nav.open(customer ? NEW_TABLET : NEW)
  }
  const editing: Editing | false | undefined =
    rec.isNew || rec.code === NEW_TABLET
      ? loaded && { mark: blank(rec.code === NEW_TABLET), isNew: true }
      : rec.row && { mark: { ...rec.row, score: rec.row.score ?? 0 }, isNew: false }
  const move = (group: Mark[], id: string, by: number) =>
    void save(async () => {
      for (const row of reorder(group, id, by)) await api.updateMark(row.id, { sortOrder: row.sortOrder })
    }, say('record.orderSaved'))

  const nameOf = (m: Pick<Mark, 'name' | 'code'>) => nameIn(m.name, lang, m.code)
  const scoreOf = (m: Mark) => (m.score ? say('record.marks.scoreN', { n: m.score }) : say('record.marks.noScore'))

  const cols = 'minmax(220px, 1.5fr) minmax(140px, 1fr) 72px'
  const row = (m: Mark, i: number, group: Mark[]) => (
    <ListRow key={m.id} actions off={!m.active} onOpen={() => open(m)}>
      <NameCell tile={<IconTile icon={m.customer ? 'message' : 'tag'} tone={m.customer ? 5 : 3} />} name={nameOf(m)} sub={m.code} />
      {!m.active ? (
        <span className="rec-cell" role="cell">
          {m.customer ? say('record.marks.tabletShort') : serviceName(m.serviceId)}
        </span>
      ) : m.customer ? (
        <span className="rec-cell" data-one={m.score ? true : undefined} role="cell">
          {scoreOf(m)}
        </span>
      ) : (
        <span className="rec-cell" data-one={m.serviceId ? true : undefined} role="cell">
          {serviceName(m.serviceId)}
        </span>
      )}
      <span role="cell">
        {m.active && (
          <MoveButtons
            name={nameOf(m)}
            first={i === 0}
            last={i === group.length - 1}
            disabled={busy}
            onMove={(by) => move(group, m.id, by)}
          />
        )}
      </span>
    </ListRow>
  )

  const lastOrder = (group: Mark[]) => (group.at(-1)?.sortOrder ?? 0) + 10

  if (editing) {
    return (
      <>
        {toast}
        <MarkDrawer
          key={rec.code}
          editing={editing}
          all={marks}
          full={full}
          services={services}
          busy={busy}
          error={error}
          onClose={rec.close}
          onSave={(draft) => {
            // A clerk's mark carries no score at all; a customer's carries one
            // always, 0 included, because an absent score leaves the stored one
            // as it was (handleUpdateMark) and 0 is how one is taken away.
            const named = { ...draft, name: cleanNames(draft.name) }
            const body: Draft = named.customer
              ? { ...named, serviceId: '', score: named.score ?? 0 }
              : { ...named, score: undefined }
            void save(
              () =>
                editing.isNew
                  ? api.createMark({
                      ...body,
                      sortOrder: lastOrder(draft.customer ? buttons : clerk),
                      ...(organisation ? { organisation } : {}),
                    })
                  : api.updateMark(draft.id!, body),
              editing.isNew ? say('record.marks.added') : say('record.saved'),
            ).then((ok) => ok && rec.close())
          }}
        />
      </>
    )
  }

  return (
    <>
      <PageHead
        title={say('admin.section.marks')}
        line={say('admin.hint.marks')}
        actions={
          <>
            <Button variant="secondary" isDisabled={!loaded || full} onPress={() => open(undefined, true)}>
              <Icon name="plus" size={16} /> {say('record.marks.addButton')}
            </Button>
            <Button variant="primary" onPress={() => open()}>
              <Icon name="plus" size={16} /> {say('record.marks.add')}
            </Button>
          </>
        }
      />
      {toast}
      {loadError && <Notice className="os-gap">{loadError}</Notice>}
      {error && !editing && <Notice className="os-gap">{error}</Notice>}

      <div className="record-split">
        <div className="record-split__main">
          <ListCard title={say('record.marks.clerk')}>
            {!loaded ? (
              <LoadingLine />
            ) : clerk.length === 0 ? (
              <EmptyState title={say('record.marks.none')} />
            ) : (
              <ListTable label={say('record.marks.clerk')} cols={cols} head={[say('record.col.mark'), say('record.offeredFor'), '']}>
                {clerk.map((m, i) => row(m, i, clerk))}
              </ListTable>
            )}
          </ListCard>

          <ListCard
            title={say('record.marks.tablet')}
            count={say('record.marks.ofFive', { n: buttons.length, max: MAX_CUSTOMER })}
            foot={full ? <p className="rec-foot">{say('record.marks.full')}</p> : undefined}
          >
            {loaded && buttons.length === 0 ? (
              <EmptyState title={say('record.marks.noButtons')} />
            ) : (
              buttons.length > 0 && (
                <ListTable label={say('record.marks.tablet')} cols={cols} head={[say('record.col.button'), say('record.col.score'), '']}>
                  {buttons.map((m, i) => row(m, i, buttons))}
                </ListTable>
              )
            )}
          </ListCard>

          {retired.length > 0 && (
            <ListCard title={say('settings.marks.retiredList')} count={say('record.retiredCount', { n: retired.length })}>
              <ListTable label={say('settings.marks.retiredList')} cols={cols} head={[say('record.col.mark'), say('record.offeredFor'), '']}>
                {retired.map((m, i) => row(m, i, retired))}
              </ListTable>
            </ListCard>
          )}
        </div>

        <div className="record-previews">
          <DeskPreview marks={clerk} services={services} lang={previewLang} setLang={setPreviewLang} />
          <TabletPreview buttons={buttons} lang={previewLang} />
        </div>
      </div>
    </>
  )
}

function DeskPreview({
  marks,
  services,
  lang,
  setLang,
}: {
  marks: Mark[]
  services: Service[]
  lang: StaffLanguage
  setLang: (next: StaffLanguage) => void
}) {
  const { lang: readerLang, say } = useStaffText()
  const offered = services.filter((s) => s.active)
  const [chosen, setChosen] = useState('')
  const [ticked, setTicked] = useState<string[]>([])
  const serviceId = chosen || offered.find((s) => marks.some((m) => m.serviceId === s.id))?.id || offered[0]?.id || ''
  const shown = marks.filter((m) => !m.serviceId || m.serviceId === serviceId)

  return (
    <Preview
      title={say('record.deskPreview')}
      controls={<LangSwitch lang={lang} setLang={setLang} />}
    >
      {offered.length > 0 && (
        <ChoiceSelect
          label={say('record.previewService')}
          value={serviceId}
          onChange={(id) => {
            setChosen(id)
            setTicked([])
          }}
          options={offered.map((s) => ({ value: s.id, label: nameIn(s.name, readerLang, s.code) }))}
        />
      )}
      <div className="record-desk" lang={lang}>
        {shown.length > 0 ? (
          <>
            <p className="record-desk__note">{t('record.marks.deskAsk', lang)}</p>
            <div className="record-desk__opts">
              {shown.map((m) => (
                <Checkbox
                  variant="secondary"
                  key={m.id}
                  isSelected={ticked.includes(m.id)}
                  onChange={() => setTicked((now) => (now.includes(m.id) ? now.filter((x) => x !== m.id) : [...now, m.id]))}
                >
                  <Checkbox.Content>
                    <Checkbox.Control>
                      <Checkbox.Indicator />
                    </Checkbox.Control>
                    {nameIn(m.name, lang, m.code)}
                  </Checkbox.Content>
                </Checkbox>
              ))}
            </div>
          </>
        ) : (
          <p className="record-desk__none">{say('record.marks.nothingOffered')}</p>
        )}
        <Button variant="primary" fullWidth>
          {t('floor.finish', lang)}
        </Button>
      </div>
    </Preview>
  )
}

/** The feedback tablet, in the words pages/Feedback shows (kioskText). */
function TabletPreview({ buttons, lang }: { buttons: Mark[]; lang: StaffLanguage }) {
  const { say } = useStaffText()
  const [pressed, setPressed] = useState('')
  return (
    <Preview title={say('record.tabletPreview')}>
      <div className="record-tablet" lang={lang}>
        {pressed ? (
          <button type="button" className="record-tablet__thanks" onClick={() => setPressed('')}>
            {kioskSay('feedbackThanks', lang)}
          </button>
        ) : buttons.length === 0 ? (
          <p className="record-desk__none">{say('record.marks.noButtons')}</p>
        ) : (
          <>
            <p className="record-tablet__q">{kioskSay('feedbackAsk', lang)}</p>
            <div className="record-tablet__buttons">
              {buttons.map((m) => (
                <button key={m.id} type="button" className="record-tablet__btn" onClick={() => setPressed(m.id)}>
                  {nameIn(m.name, lang, m.code)}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </Preview>
  )
}

function MarkDrawer({
  editing,
  all,
  full,
  services,
  busy,
  error,
  onClose,
  onSave,
}: {
  editing: Editing
  all: Mark[]
  full: boolean
  services: Service[]
  busy: boolean
  error: string
  onClose: () => void
  onSave: (draft: Draft) => void
}) {
  const { lang, say } = useStaffText()
  const { isNew } = editing
  const [draft, setDraft] = useState(editing.mark)
  const code = draft.code ?? ''
  const customer = Boolean(draft.customer)
  const taken = code !== '' && all.some((m) => m.id !== draft.id && m.code === code)
  // Bringing a retired button back, or adding one, needs a free place among the five.
  const noRoom = customer && full && (isNew || !editing.mark.active)
  const valid = CODE_RE.test(code) && !taken && hasName(draft.name) && !(noRoom && draft.active)

  return (
    <InnerPage
      back={say('admin.section.marks')}
      title={
        isNew
          ? say(customer ? 'record.marks.newButton' : 'record.marks.new')
          : nameIn(draft.name, lang, code)
      }
      onClose={onClose}
      valid={valid}
      busy={busy}
      submitLabel={isNew ? say(customer ? 'record.marks.addButton' : 'record.marks.add') : say('record.save')}
      onSubmit={() => onSave(draft)}
      dirty={(draft.score ?? 0) !== (editing.mark.score ?? 0)}
    >
      {error && <Notice>{error}</Notice>}
      {isNew ? (
        <fieldset className="field">
          <legend>{say('record.marks.whoPresses')}</legend>
          <RadioGroup
            className="record-kind"
            name="record-kind"
            aria-label={say('record.marks.whoPresses')}
            value={customer ? 'customer' : 'clerk'}
            onChange={(next) =>
              setDraft(next === 'customer' ? { ...draft, customer: true, serviceId: '' } : { ...draft, customer: false, score: 0 })
            }
          >
            <Radio value="clerk">
              <Radio.Content>
                <Radio.Control>
                  <Radio.Indicator />
                </Radio.Control>
                <strong>{say('record.marks.byClerk')}</strong>
              </Radio.Content>
            </Radio>
            <Radio value="customer" isDisabled={full}>
              <Radio.Content>
                <Radio.Control>
                  <Radio.Indicator />
                </Radio.Control>
                <span className="record-kind__text">
                  <strong title={say('record.marks.fixedLater')}>{say('record.marks.byCustomer')}</strong>
                  {full && <small>{say('record.marks.full')}</small>}
                </span>
              </Radio.Content>
            </Radio>
          </RadioGroup>
        </fieldset>
      ) : (
        <p className="record-fixed">{say(customer ? 'record.marks.fixedTablet' : 'record.marks.fixedClerk')}</p>
      )}

      <NamesField
        legend={say(customer ? 'record.marks.buttonText' : 'record.name')}
        value={draft.name}
        onChange={(name) => setDraft({ ...draft, name })}
      />
      <CodeField
        value={code}
        onChange={(next) => setDraft({ ...draft, code: next })}
        taken={taken}
        isNew={isNew}
        placeholder={customer ? EXAMPLE_CUSTOMER : EXAMPLE_CLERK}
      />

      {customer ? (
        <fieldset className="field">
          <legend title={say('record.marks.scoreHint')}>{say('record.marks.score')}</legend>
          <Seg
            label={say('record.marks.score')}
            value={String(draft.score ?? 0)}
            onChange={(n) => setDraft({ ...draft, score: Number(n) })}
            options={[0, 1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: n === 0 ? say('record.marks.none0') : n }))}
          />
        </fieldset>
      ) : (
        <ServiceScope
          services={services}
          value={draft.serviceId ?? ''}
          onChange={(serviceId) => setDraft({ ...draft, serviceId })}
        />
      )}

      {!isNew && (
        <Toggle
          title={say(customer ? 'record.marks.onTablets' : 'record.offeredAtDesk')}
          hint={say('record.retireHint')}
          status={noRoom ? say('record.marks.full') : undefined}
          checked={draft.active ?? true}
          disabled={noRoom && !draft.active}
          onChange={(active) => setDraft({ ...draft, active })}
        />
      )}
    </InnerPage>
  )
}
